import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PageHeader from "@/components/common/PageHeader";
import { toast } from "sonner";
import { Save, Upload, Globe, Image as ImageIcon, FileType, Settings as SettingsIcon, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function SiteSettings() {
  const [form, setForm] = useState({
    siteTitle: "",
    siteLogoUrl: "",
    faviconUrl: "",
    metaTitle: "",
    metaDescription: "",
    footerText: ""
  });
  const [recordId, setRecordId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoName, setLogoName] = useState("No file selected");
  const [faviconName, setFaviconName] = useState("No file selected");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/SiteSetting');
        if (res.success && res.data && res.data.length > 0) {
          const data = res.data[0];
          setRecordId(data.id);
          setForm({
            siteTitle: data.siteTitle || "",
            siteLogoUrl: data.siteLogoUrl || "",
            faviconUrl: data.faviconUrl || "",
            metaTitle: data.metaTitle || "",
            metaDescription: data.metaDescription || "",
            footerText: data.footerText || ""
          });
          if (data.siteLogoUrl) {
            setLogoName(data.siteLogoUrl.split('/').pop());
          }
          if (data.faviconUrl) {
            setFaviconName(data.faviconUrl.split('/').pop());
          }
        }
      } catch (err) {
        toast.error("Failed to load Site Settings");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoName(file.name);
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await api.post('/SiteSetting/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.success) {
        setForm({ ...form, siteLogoUrl: res.data });
        toast.success("Logo uploaded");
      }
    } catch (err) {
      toast.error(err.message || "Failed to upload logo");
    }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFaviconName(file.name);
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await api.post('/SiteSetting/upload-favicon', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.success) {
        setForm({ ...form, faviconUrl: res.data });
        toast.success("Favicon uploaded");
      }
    } catch (err) {
      toast.error(err.message || "Failed to upload favicon");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (recordId) {
        const res = await api.put(`/SiteSetting/${recordId}`, form);
        if (res.success) toast.success("Site settings updated.");
      } else {
        const res = await api.post('/SiteSetting', form);
        if (res.success) {
          setRecordId(res.data);
          toast.success("Site settings created.");
        }
      }
    } catch (err) {
      toast.error(err.message || "Failed to save settings");
    }
  };

  if (loading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Site Settings"
        description="Global identity and SEO defaults for your public portfolio."
        testId="settings"
      />

      <form onSubmit={handleSave} data-testid="settings-form">
        <Tabs defaultValue="general" className="space-y-5">
          <TabsList>
            <TabsTrigger value="general" data-testid="settings-tab-general">
              <SettingsIcon className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
              General
            </TabsTrigger>
            <TabsTrigger value="seo" data-testid="settings-tab-seo">
              <Globe className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
              SEO
            </TabsTrigger>
            <TabsTrigger value="footer" data-testid="settings-tab-footer">
              <FileType className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
              Footer
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general">
            <Card className="p-6 space-y-5">
              <div>
                <h3 className="font-display text-sm font-semibold">Site identity</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Core branding shown across your portfolio.</p>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Site Title</Label>
                <Input data-testid="settings-site-title" value={form.siteTitle} onChange={(e) => setForm({ ...form, siteTitle: e.target.value })} className="mt-1.5" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <UploadCard
                  label="Logo"
                  description="SVG or PNG. Min 200×200."
                  fileName={logoName}
                  testid="settings-logo-upload"
                  icon={ImageIcon}
                  onChange={handleLogoUpload}
                />
                <UploadCard
                  label="Favicon"
                  description=".ico or .png. 32×32 recommended."
                  fileName={faviconName}
                  testid="settings-favicon-upload"
                  icon={ImageIcon}
                  onChange={handleFaviconUpload}
                />
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="seo">
            <Card className="p-6 space-y-5">
              <div>
                <h3 className="font-display text-sm font-semibold">SEO defaults</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Used when individual pages don&apos;t override.</p>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Meta Title</Label>
                <Input data-testid="settings-meta-title" value={form.metaTitle} onChange={(e) => setForm({ ...form, metaTitle: e.target.value })} className="mt-1.5" />
                <p className="text-[11px] font-mono text-muted-foreground mt-1">{form.metaTitle?.length || 0}/60 characters</p>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Meta Description</Label>
                <Textarea data-testid="settings-meta-description" value={form.metaDescription} onChange={(e) => setForm({ ...form, metaDescription: e.target.value })} rows={3} className="mt-1.5" />
                <p className="text-[11px] font-mono text-muted-foreground mt-1">{form.metaDescription?.length || 0}/160 characters</p>
              </div>
              <div className="rounded-md border border-border p-4 bg-muted/30">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Search preview</p>
                <p className="text-[11px] text-muted-foreground font-mono truncate">folioadmin.io</p>
                <p className="text-base text-primary mt-0.5 line-clamp-1">{form.metaTitle || "Site Title"}</p>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{form.metaDescription || "Site Description"}</p>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="footer">
            <Card className="p-6 space-y-5">
              <div>
                <h3 className="font-display text-sm font-semibold">Footer</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Customize the line shown at the bottom of every page.</p>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Footer Text</Label>
                <Input data-testid="settings-footer-text" value={form.footerText} onChange={(e) => setForm({ ...form, footerText: e.target.value })} className="mt-1.5" />
              </div>
              <div className="rounded-md border border-border p-6 text-center bg-muted/30">
                <p className="text-xs font-mono text-muted-foreground">{form.footerText}</p>
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="flex items-center justify-end gap-2 mt-6">
          <Button variant="outline" type="button">Cancel</Button>
          <Button type="submit" data-testid="settings-save">
            <Save className="mr-1.5 h-4 w-4" strokeWidth={1.75} />
            Save settings
          </Button>
        </div>
      </form>
    </div>
  );
}

const UploadCard = ({ label, description, fileName, testid, icon: Icon, onChange }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
    <label className="mt-1.5 block cursor-pointer">
      <div className="flex items-center gap-3 border border-dashed border-border rounded-md px-3 py-3 hover:border-foreground/30 transition-colors">
        <span className="h-10 w-10 rounded-md bg-muted flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{fileName !== "No file selected" ? fileName : "Click to upload"}</p>
          <p className="text-[11px] font-mono text-muted-foreground truncate">{description}</p>
        </div>
        <Upload className="h-3.5 w-3.5 text-primary shrink-0" strokeWidth={1.75} />
      </div>
      <input type="file" className="hidden" data-testid={testid} onChange={onChange} />
    </label>
  </div>
);
